import { t } from "ttag";
import _ from "underscore";

import type {
  DatabaseId,
  Field,
  FieldId,
  Table,
  TemplateTags,
} from "metabase-types/api";

import { databaseID } from "./database";
import {
  templateTags,
  withDifferentDatabase,
  withTemplateTags,
} from "./native";
import type { MetadataProvider, Query } from "./types";

export function nativeQueryFieldIds(query: Query): FieldId[] {
  return [
    ...new Set(
      Object.values(templateTags(query)).flatMap((tag) => {
        if (!tag.dimension) {
          return [];
        }
        const [kind, id, options] = tag.dimension;
        if (kind !== "field" || typeof id !== "number") {
          throw new Error(
            t`Parameter ${tag.name} does not reference a database field.`,
          );
        }
        return options?.["source-field"] != null
          ? [id, options["source-field"]]
          : [id];
      }),
    ),
  ];
}

export function matchingNativeQueryTable(
  source: Table,
  databaseId: DatabaseId,
  tables: Table[],
): Table {
  const name = [source.schema, source.name].filter(Boolean).join(".");
  const matches = tables.filter(
    (table) =>
      table.db_id === databaseId &&
      table.active &&
      table.name === source.name &&
      (table.schema ?? "") === (source.schema ?? ""),
  );
  if (matches.length === 0) {
    throw new Error(
      t`Table ${name} was not found in the selected database's metadata.`,
    );
  }
  if (matches.length > 1) {
    throw new Error(
      t`More than one table matches ${name} in the selected database.`,
    );
  }
  return matches[0];
}

function fieldPath(field: Field, table: Table): string[] {
  const path = [field.name];
  const visited = new Set([field.id]);
  while (field.parent_id != null) {
    const parent = table.fields?.find(
      (candidate) => candidate.id === field.parent_id,
    );
    if (!parent || visited.has(parent.id)) {
      throw new Error(
        t`The metadata for nested field ${field.name} is incomplete.`,
      );
    }
    visited.add(parent.id);
    path.unshift(parent.name);
    field = parent;
  }
  return path;
}

function matchingFieldId(
  id: FieldId,
  sourceDatabaseId: DatabaseId,
  targetDatabaseId: DatabaseId,
  sourceTables: Table[],
  targetTables: Table[],
): FieldId {
  const sourceTable = sourceTables.find(
    (table) =>
      table.db_id === sourceDatabaseId &&
      table.fields?.some((field) => field.id === id),
  );
  const source = sourceTable?.fields?.find((field) => field.id === id);
  if (!source || !sourceTable || !source.active) {
    throw new Error(
      t`Field ${id} was not found in the current database's metadata.`,
    );
  }
  const targetTable = matchingNativeQueryTable(
    sourceTable,
    targetDatabaseId,
    targetTables,
  );
  const name = [
    sourceTable.schema,
    sourceTable.name,
    ...fieldPath(source, sourceTable),
  ]
    .filter(Boolean)
    .join(".");
  const matches = (targetTable.fields ?? []).filter(
    (field) =>
      field.active &&
      field.name === source.name &&
      field.table_id === targetTable.id &&
      _.isEqual(field.nfc_path ?? null, source.nfc_path ?? null) &&
      _.isEqual(fieldPath(field, targetTable), fieldPath(source, sourceTable)),
  );
  if (matches.length === 0) {
    throw new Error(
      t`Field ${name} was not found in the selected database's metadata.`,
    );
  }
  if (matches.length > 1) {
    throw new Error(
      t`More than one field matches ${name} in the selected database.`,
    );
  }
  const target = matches[0];
  if (
    source.base_type !== target.base_type ||
    (source.effective_type ?? source.base_type) !==
      (target.effective_type ?? target.base_type) ||
    (source.coercion_strategy ?? null) !== (target.coercion_strategy ?? null)
  ) {
    throw new Error(
      t`Field ${name} has a different type in the selected database.`,
    );
  }
  if (typeof target.id !== "number") {
    throw new Error(t`Field ${name} does not have a database field ID.`);
  }
  return target.id;
}

export function withMappedNativeQueryDatabase(
  query: Query,
  databaseId: DatabaseId,
  metadata: MetadataProvider,
  sourceTables: Table[],
  targetTables: Table[],
): Query {
  const sourceDatabaseId = databaseID(query);
  if (sourceDatabaseId === databaseId) {
    return query;
  }
  if (sourceDatabaseId == null) {
    throw new Error(t`Select a source database before mapping fields.`);
  }
  const tags = templateTags(query);
  const fieldIds = new Map(
    nativeQueryFieldIds(query).map((id) => [
      id,
      matchingFieldId(
        id,
        sourceDatabaseId,
        databaseId,
        sourceTables,
        targetTables,
      ),
    ]),
  );
  const mappedTags: TemplateTags = Object.fromEntries(
    Object.entries(tags).map(([name, tag]) => {
      if (!tag.dimension) {
        return [name, tag];
      }
      const [kind, id, options] = tag.dimension;
      const mappedOptions =
        options?.["source-field"] != null
          ? {
              ...options,
              "source-field": fieldIds.get(options["source-field"])!,
            }
          : options;
      return [
        name,
        { ...tag, dimension: [kind, fieldIds.get(id)!, mappedOptions] },
      ];
    }),
  );
  return withTemplateTags(
    withDifferentDatabase(query, databaseId, metadata),
    mappedTags,
  );
}
